import { Link, router } from "@inertiajs/react"
import { BackofficeLayout } from "@/components/layout/BackofficeLayout"
import { SearchInput } from "@/components/shared/SearchInput"
import { Pagination, PaginationMeta } from "@/components/shared/Pagination"
import { Badge } from "@/components/ui/badge"
import type { BackofficeNotarization } from "@/types"

function StageBadge({ stage }: { stage: string }) {
  const config: Record<string, { label: string; className: string }> = {
    application: { label: "Application", className: "bg-secondary text-secondary-foreground" },
    payment: { label: "Payment", className: "bg-yellow-600 text-white" },
    scheduling: { label: "Scheduling", className: "bg-blue-600 text-white" },
    scheduled: { label: "Scheduled", className: "bg-indigo-600 text-white" },
    in_progress: { label: "In Progress", className: "bg-purple-600 text-white" },
    completed: { label: "Completed", className: "bg-green-600 text-white" },
    cancelled: { label: "Cancelled", className: "bg-gray-600 text-white" },
  }
  const { label, className } = config[stage] || { label: stage, className: "bg-secondary text-secondary-foreground" }
  return <Badge className={`font-normal text-xs ${className}`}>{label}</Badge>
}

type Props = {
  notarizations: BackofficeNotarization[]
  search: string
  pagination: PaginationMeta
}

export default function BackofficeNotarizationsIndex({ notarizations, search, pagination }: Props) {
  return (
    <BackofficeLayout title="Notarizations" breadcrumbs={[{ label: "Backoffice", href: "/backoffice/dashboard" }, { label: "Notarizations" }]}>
      <div className="mb-8">
        <h1 className="text-xl font-semibold tracking-tight">Notarizations</h1>
        <p className="text-sm text-muted-foreground">{pagination.total} {pagination.total === 1 ? "notarization" : "notarizations"}</p>
      </div>

      <SearchInput value={search} placeholder="Search notarizations..." />

      <div className="mt-4">
        {notarizations.length === 0 ? (
          <div className="border rounded-lg py-16 text-center">
            <h3 className="text-sm font-medium text-foreground mb-1">No notarizations yet</h3>
          </div>
        ) : (
          <div className="border rounded-lg">
            {notarizations.map((n, i) => (
              <div
                key={n.id}
                className={`px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors ${i > 0 ? "border-t" : ""}`}
                onClick={() => router.visit(`/backoffice/notarizations/${n.id}`)}
              >
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <Link href={`/backoffice/notarizations/${n.id}`} className="text-sm font-medium hover:underline">{n.business_name}</Link>
                      <span className="text-[12px] text-muted-foreground">{n.type_name}</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {n.account_name} · {n.jurisdiction_label || "\u2014"} · <span className="capitalize">{n.session_mode.replace(/_/g, ' ')}</span>{n.notary_name ? ` · ${n.notary_name}` : ''}
                    </div>
                  </div>
                  <div className="ml-4">
                    <StageBadge stage={n.stage} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-4">
        <Pagination pagination={pagination} />
      </div>
    </BackofficeLayout>
  )
}
